<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  onMounted,
  defineEmits,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

// [VITE] [TODO] We are using vue-virtual-scroll for now, since that seemed the simplest way to migrate
// from the current one. But we should consider using tanstack virtual in the future
// https://tanstack.com/virtual/latest/docs/framework/vue/examples/variable
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import ChatListHeader from './ChatListHeader.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import ConversationItem from './ConversationItem.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import IntersectionObserver from './IntersectionObserver.vue';
import Thumbnail from './widgets/Thumbnail.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import ContactAPI from 'dashboard/api/contacts';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useEventListener } from '@vueuse/core';

import { emitter } from 'shared/helpers/mitt';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import wootAPI from '@/v3/api/apiClient';

import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  hasSelectedConversation: { default: false, type: Boolean },
  isMobileActive: { default: true, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings, updateUISettings } = useUISettings();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const conversationListRef = ref(null);
const conversationDynamicScroller = ref(null);
const bulkActionsRef = ref(null);

// Initialize with TODOS, will be updated in onMounted when all reactive values are available
const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.MINHAS);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const showAdvancedFilters = ref(false);
const showBulkActionsModal = ref(false);
const modalView = ref('main');
const modalPosition = ref({ top: 0, right: 0 });
const transitionName = ref('slide-left');
// Selection mode state - keeps bulk actions visible even with 0 selections
const isSelectionMode = ref(false);
// Confirmation state for destructive actions
const confirmationState = ref({
  action: null, // The action to confirm
  message: '',  // Confirmation message
  onConfirm: null, // Callback to execute on confirmation
  data: null // Any additional data needed for the action
});
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const isContextMenuOpen = ref(false);
const appliedFilter = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);
const agentNamesByInbox = ref(new Map());

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getAllConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const comIAChatsList = useMapGetter('getComIAChats');
const atendimentoChatsList = useMapGetter('getAtendimentoChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentRole = useMapGetter('getCurrentRole');
const isFeatureEnabledonAccount = useMapGetter('accounts/isFeatureEnabledonAccount');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  selectRangeConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onAssignTeamsForBulk,
  onUpdateConversations,
  onUpdatePriority,
  onRemoveLabels,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

// Function to determine the default tab based on user role and feature flags
function getDefaultTab() {
  // Fallback to TODOS if reactive values are not yet available
  if (!currentRole.value || !currentAccountId.value) {
    return wootConstants.ASSIGNEE_TYPE.TODOS;
  }
  
  // If agent and hide_all_chats_for_agent is enabled, default to "Minhas"
  if (currentRole.value !== 'administrator' && 
      isFeatureEnabledonAccount.value(currentAccountId.value, 'hide_all_chats_for_agent')) {
    return wootConstants.ASSIGNEE_TYPE.MINHAS;
  }
  // Otherwise default to "Todos" (current behavior)
  return wootConstants.ASSIGNEE_TYPE.TODOS;
}

// computed
const intersectionObserverOptions = computed(() => {
  return {
    root: conversationListRef.value,
    rootMargin: '100px 0px 100px 0px',
  };
});

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const hideAllChatsForAgents = computed(() => {
  // manager concept: Manager is exempt from this restriction
  // Original: currentRole.value !== 'administrator' && isFeatureEnabledonAccount.value(...)
  if (currentRole.value === 'manager') return false;

  return (
    currentRole.value !== 'administrator' &&
    isFeatureEnabledonAccount.value(
      currentAccountId.value,
      'hide_all_chats_for_agent'
    )
  );
});

const assigneeTabItems = computed(() => {
  let filteredTabs = filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  );

  // Hide "todos" (all chats) tab for agents if feature is enabled
  if (hideAllChatsForAgents.value) {
    filteredTabs = filteredTabs.filter(tab => tab.key !== 'todos');
  }

  return filteredTabs.map(({ key, count: countKey }) => {
    const tabNames = {
      todos: 'Todas',
      atendimento: 'Atendimento',
      com_ia: 'I.A',
      minhas: 'Minhas'
    };
    
    const count = (conversationStats.value && conversationStats.value[countKey]) || 0;
    
    // Log count updates for Atendimento tab
    
    return {
      key,
      name: tabNames[key] || key,
      count,
    };
  });
});

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.TODOS ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ATENDIMENTO ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.COM_IA
  );
});

const currentPageFilterKey = computed(() => {
  if (hasAppliedFiltersOrActiveFolders.value) {
    return 'appliedFilters';
  }
  
  // Map frontend tab types to pagination keys that exist in conversationPage store
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ATENDIMENTO) {
    return 'atendimento';
  } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.TODOS) {
    return 'all';
  } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.MINHAS) {
    return 'me';
  } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.COM_IA) {
    return 'com_ia';
  }
  
  return activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
// Create a computed pagination key for currentPage that matches currentPageFilterKey
const paginationKey = computed(() => {
  // Map frontend tab types to pagination keys that exist in conversationPage store
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ATENDIMENTO) {
    return 'atendimento';
  } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.TODOS) {
    return 'all';
  } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.MINHAS) {
    return 'me';
  } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.COM_IA) {
    return 'com_ia';
  }
  return activeAssigneeTab.value;
});

const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  paginationKey
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  const tabItem = assigneeTabItems.value.find(
    item => item.key === activeAssigneeTab.value
  );
  return tabItem ? tabItem.count : 0;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    !chatsOnView.value.length;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

const conversationFilters = computed(() => {
  // Add label filter for Atendimento tab and map assigneeType to backend values
  const baseLabels = props.label ? [props.label] : [];
  let labelsFilter = [...baseLabels];
  let assigneeTypeForBackend = activeAssigneeTab.value;
  
  // Map frontend tab types to backend assignee types
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ATENDIMENTO) {
    labelsFilter = [...new Set([...labelsFilter, 'atendimento-humano'])];
    
    // For conversation loading, ALSO use role-based assignee type to match stats
    // This ensures consistency between count and loaded conversations
    if (currentRole.value !== 'administrator' && 
        isFeatureEnabledonAccount.value(currentAccountId.value, 'hide_all_chats_for_agent')) {
      assigneeTypeForBackend = 'me'; // Only load assigned conversations with atendimento label
    } else {
      assigneeTypeForBackend = 'atendimento'; // Load all conversations with atendimento label
    }
  } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.TODOS) {
    assigneeTypeForBackend = 'all';
  } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.MINHAS) {
    assigneeTypeForBackend = 'me';
  } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.COM_IA) {
    assigneeTypeForBackend = 'com_ia'; // Backend will handle com_ia type
  }
  
  return {
    inboxId: props.conversationInbox ? props.conversationInbox : undefined,
    assigneeType: assigneeTypeForBackend,
    status: activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    labels: labelsFilter.length ? labelsFilter : undefined,
    teamId: props.teamId || undefined,
    conversationType: props.conversationType || undefined,
  };
});

const statsFilters = computed(() => {
  // Get base labels from props
  let labelsForStats = props.label ? [props.label] : [];

  // If there are applied filters with labels, include them
  if (hasAppliedFilters.value && appliedFilters.value.length > 0) {
    const labelFilters = appliedFilters.value.filter(f =>
      f.attributeKey === 'labels' || f.attribute_key === 'labels'
    );

    if (labelFilters.length > 0) {
      labelFilters.forEach(filter => {
        if (filter.values && Array.isArray(filter.values)) {
          filter.values.forEach(val => {
            const labelName = typeof val === 'string' ? val : val.name || val.title || val;
            if (labelName && !labelsForStats.includes(labelName)) {
              labelsForStats.push(labelName);
            }
          });
        }
      });
    }
  }

  return {
    status: activeStatus.value,
    inboxId: props.conversationInbox || undefined,
    teamId: props.teamId || undefined,
    conversationType: props.conversationType || undefined,
    labels: labelsForStats.length > 0 ? labelsForStats : undefined,
  };
});


const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === 'mention') {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (props.conversationType === 'participating') {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === 'unattended') {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

const conversationList = computed(() => {
  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.TODOS) {
      localConversationList = [...allChatList.value(filters)];
    } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ATENDIMENTO) {
      // Use dedicated Atendimento getter that respects backend filtering
      localConversationList = [...atendimentoChatsList.value(filters)];
    } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.COM_IA) {
      // Use dedicated Com IA getter that respects backend filtering
      localConversationList = [...comIAChatsList.value(filters)];
    } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.MINHAS) {
      localConversationList = [...mineChatsList.value(filters)];
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }
  return localConversationList;
});

const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// Determine if the tabs/bulk actions bar should be shown
const shouldShowTabsBar = computed(() => {
  // Always show if in selection mode (to display bulk actions)
  if (isSelectionMode.value) {
    return true;
  }
  // Show if no filters are active (to display tabs)
  if (!hasAppliedFiltersOrActiveFolders.value) {
    return true;
  }
  // Hide if filters are active and not in selection mode
  return false;
});

// Computed properties for bulk actions modal data
const assignableAgents = computed(() => {
  // Always include "Nenhum" option first
  const noneOption = { id: null, name: 'Nenhum', email: 'None' };

  // Get agents that can be assigned to selected inboxes
  if (uniqueInboxes.value.length === 0) {
    return [noneOption];
  }

  const agents = store.getters['inboxAssignableAgents/getAssignableAgents'](
    uniqueInboxes.value.join(',')
  ) || [];

  return [
    noneOption,
    ...agents
  ];
});

const allTeams = computed(() => {
  return [
    { id: 0, name: 'Nenhuma' },
    ...(teamsList.value || [])
  ];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status, order_by: orderBy } = filterBy;
  activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;
  activeSortBy.value =
    Object.keys(wootConstants.SORT_BY_TYPE).find(
      sortField => sortField === orderBy
    ) || wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
  // [VITE] removing this since the library has changed
  // nextTick(() => {
  //   // Addressing a known issue in the virtual list library where dynamically added items
  //   // might not render correctly. This workaround involves a slight manual adjustment
  //   // to the scroll position, triggering the list to refresh its rendering.
  //   const virtualList = conversationListRef.value;
  //   const scrollToOffset = virtualList?.scrollToOffset;
  //   const currentOffset = virtualList?.getOffset() || 0;
  //   if (scrollToOffset) {
  //     scrollToOffset(currentOffset + 1);
  //   }
  // });
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    languages: languages,
    countries: countries,
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

function fetchConversations() {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function resetAndFetchData() {
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  
  // Fetch initial stats
  store.dispatch('conversationStats/getAllStats', statsFilters.value);
  
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations();
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// Add a method to handle scroll events
function handleScroll() {
  const scroller = conversationDynamicScroller.value;
  if (scroller && scroller.hasScrollbar) {
    const { scrollTop, scrollHeight, clientHeight } = scroller.$el;
    if (scrollHeight - (scrollTop + clientHeight) < 100) {
      loadMoreConversations();
    }
  }
}

function updateAssigneeTab(selectedTab) {
  if (activeAssigneeTab.value !== selectedTab) {
    resetBulkActions();
    emitter.emit('clearSearchInput');
    
    // Clear selected conversation when switching to Com IA tab to prevent contamination
    if (selectedTab === wootConstants.ASSIGNEE_TYPE.COM_IA) {
      store.dispatch('clearSelectedState');
      
      // Also navigate away from current conversation URL to prevent auto-selection
      if (route.params && route.params.conversation_id) {
        const {
          params: { accountId, inbox_id: inboxId, label, teamId },
        } = route;
        router.push(
          conversationListPageURL({
            accountId,
            conversationType: props.conversationType,
            customViewId: props.foldersId,
            inboxId,
            label,
            teamId,
          })
        );
      }
    }
    
    activeAssigneeTab.value = selectedTab;
    
    // Log the stats filters when switching to Atendimento tab
    if (selectedTab === wootConstants.ASSIGNEE_TYPE.ATENDIMENTO) {
    }
    
    // For Com IA tab, always reset to prevent cross-tab contamination
    // For other tabs, use regular fetch to maintain performance
    if (selectedTab === wootConstants.ASSIGNEE_TYPE.COM_IA) {
      resetAndFetchData();
    } else {
      fetchConversations();
    }
    
    // Refresh stats when switching tabs to ensure counts are accurate
    emitter.emit('fetch_conversation_stats');
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

function toggleConversationLayout() {
  const { show_conversation_list: showConversationList = true } = uiSettings.value;
  updateUISettings({
    show_conversation_list: !showConversationList,
  });
}

// Modal action handlers
function onToggleLabelActions() {
  if (bulkActionsRef.value) {
    bulkActionsRef.value.toggleLabelActions();
  }
}

function onToggleUpdateActions() {
  if (bulkActionsRef.value) {
    bulkActionsRef.value.toggleUpdateActions();
  }
}

function onToggleAgentList() {
  if (bulkActionsRef.value) {
    bulkActionsRef.value.toggleAgentList();
  }
}

function onToggleTeamsList() {
  if (bulkActionsRef.value) {
    bulkActionsRef.value.toggleTeamsList();
  }
}

// Modal view handlers
function handleShowMoreActions(event) {
  // Get the button's position
  const button = event.currentTarget;
  const rect = button.getBoundingClientRect();

  // Calculate position for modal (aligned with button base)
  modalPosition.value = {
    top: rect.bottom, // Align with button bottom
    right: window.innerWidth - rect.right // Align to right edge of button
  };

  showBulkActionsModal.value = true;
  modalView.value = 'main';

  // Fetch agents if needed
  if (uniqueInboxes.value.length > 0) {
    store.dispatch('inboxAssignableAgents/fetch', uniqueInboxes.value);
  }
}

function showModalView(view) {
  // Determine transition direction
  if (view === 'main') {
    transitionName.value = 'slide-right';
  } else {
    transitionName.value = 'slide-left';
  }

  modalView.value = view;

  // Fetch agents when opening agents view
  if (view === 'agents' && uniqueInboxes.value.length > 0) {
    store.dispatch('inboxAssignableAgents/fetch', uniqueInboxes.value);
  }
}

function resetModal() {
  modalView.value = 'main';
  showBulkActionsModal.value = false;
  confirmationState.value = {
    action: null,
    message: '',
    onConfirm: null,
    data: null
  };
}

function showConfirmation(action, message, onConfirm, data = null) {
  confirmationState.value = {
    action,
    message,
    onConfirm,
    data
  };
  showModalView('confirmation');
}

function executeConfirmedAction() {
  if (confirmationState.value.onConfirm) {
    confirmationState.value.onConfirm();
  }
  resetModal();
  exitSelectionMode();
}

function cancelConfirmation() {
  confirmationState.value = {
    action: null,
    message: '',
    onConfirm: null,
    data: null
  };
  showModalView('main');
}

function handleLabelSelect(label) {
  onAssignLabels([label.title]);
  resetModal();
  exitSelectionMode();
}

function handleAgentSelect(agent) {
  onAssignAgent(agent);
  resetModal();
  exitSelectionMode();
}

function handleTeamSelect(team) {
  onAssignTeamsForBulk(team);
  resetModal();
  exitSelectionMode();
}

function handleStatusSelect(status) {
  onUpdateConversations(status);
  resetModal();
  exitSelectionMode();
}

function handlePrioritySelect(priority) {
  onUpdatePriority(priority);
  resetModal();
  exitSelectionMode();
}

async function handleRemoveAllLabels() {
  // First check if any conversations are selected
  if (!selectedConversations.value || selectedConversations.value.length === 0) {
    resetModal();
    return;
  }

  const message = `Tem certeza que deseja remover todos os marcadores?`;

  showConfirmation('removeAllLabels', message, async () => {
    // Collect all unique labels from selected conversations
    const labelsToRemove = new Set();

    selectedConversations.value.forEach(convId => {
      const conversation = store.getters.getConversationById(convId);
      if (conversation?.labels && Array.isArray(conversation.labels)) {
        conversation.labels.forEach(label => {
          // Skip "atendimento-humano" label - it should be preserved
          if (label !== 'atendimento-humano') {
            labelsToRemove.add(label);
          }
        });
      }
    });

    // Convert Set to Array and call remove labels action
    const labelsArray = Array.from(labelsToRemove);
    if (labelsArray.length > 0) {
      await onRemoveLabels(labelsArray);
    } else {
      useAlert(t('BULK_ACTION.LABELS.NO_LABELS_TO_REMOVE'));
    }
  });
}

async function handleRemoveSingleLabel(labelTitle) {
  // Check if any conversations are selected
  if (!selectedConversations.value || selectedConversations.value.length === 0) {
    resetModal();
    return;
  }

  const message = `Tem certeza que deseja remover o marcador "${labelTitle}"?`;

  showConfirmation('removeSingleLabel', message, async () => {
    // Remove the specific label from selected conversations
    await onRemoveLabels([labelTitle]);
  }, { label: labelTitle });
}

async function handleEnableAI() {
  // Check if any conversations are selected
  if (!selectedConversations.value || selectedConversations.value.length === 0) {
    resetModal();
    return;
  }

  const message = `Tem certeza que deseja ativar a IA? O atendimento humano será desativado.`;

  showConfirmation('enableAI', message, async () => {
    // Remove "atendimento-humano" tag to enable AI
    await onRemoveLabels(['atendimento-humano']);
    useAlert(t('BULK_ACTION.AI.ENABLED_SUCCESFUL'));
  });
}

async function handleDisableAI() {
  // Check if any conversations are selected
  if (!selectedConversations.value || selectedConversations.value.length === 0) {
    resetModal();
    return;
  }

  const message = `Tem certeza que deseja desativar a IA? O atendimento será transferido para humanos.`;

  showConfirmation('disableAI', message, async () => {
    // Add "atendimento-humano" tag to disable AI (enable human service)
    await onAssignLabels(['atendimento-humano']);
    useAlert(t('BULK_ACTION.AI.DISABLED_SUCCESFUL'));
  });
}

async function handleDeleteContacts() {
  // Check if any conversations are selected
  if (!selectedConversations.value || selectedConversations.value.length === 0) {
    resetModal();
    return;
  }

  // Extract unique contact IDs from selected conversations
  const contactIds = [...new Set(
    selectedConversations.value
      .map(convId => store.getters.getConversationById(convId))
      .filter(conv => conv?.meta?.sender?.id)
      .map(conv => conv.meta.sender.id)
  )];

  if (contactIds.length === 0) {
    useAlert('Nenhum contato encontrado nas conversas selecionadas');
    resetModal();
    return;
  }

  const message = `Tem certeza que deseja excluir permanentemente ${contactIds.length} contato${contactIds.length > 1 ? 's' : ''}? Esta ação não pode ser desfeita.`;

  showConfirmation('deleteContacts', message, async () => {
    try {
      const response = await ContactAPI.batchDestroy(contactIds);
      useAlert(`${response.data.deleted_count || contactIds.length} contatos excluídos com sucesso`);

      // Check if current conversation is one of the deleted ones BEFORE clearing selections
      const currentConversationId = route.params?.conversation_id;
      const isCurrentConversationDeleted = currentConversationId &&
        selectedConversations.value.includes(parseInt(currentConversationId));

      // Remove deleted conversations from the store to prevent rendering issues
      selectedConversations.value.forEach(convId => {
        store.dispatch('deleteConversation', convId);
      });

      // Clear selections and exit selection mode
      resetBulkActions();
      exitSelectionMode();

      // Navigate to dashboard if current conversation was deleted
      if (isCurrentConversationDeleted) {
        const accountId = route.params?.accountId || currentAccountId.value;
        router.push(`/app/accounts/${accountId}/dashboard`);
      }

      // Add a longer delay to allow async deletion to fully propagate
      // This helps prevent race conditions with backend async operations
      setTimeout(() => {
        resetAndFetchData();
      }, 1500);
    } catch (error) {
      console.error('Error deleting contacts:', error);
      useAlert(error.response?.data?.error || 'Erro ao excluir contatos');
    }
  });
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    const {
      params: { accountId, inbox_id: inboxId, label, teamId },
      name,
    } = useRoute();
    let conversationType = '';
    if (isOnMentionsView({ route: { name } })) {
      conversationType = 'mention';
    } else if (isOnUnattendedView({ route: { name } })) {
      conversationType = 'unattended';
    }
    router.push(
      conversationListPageURL({
        accountId,
        conversationType: conversationType,
        customViewId: props.foldersId,
        inboxId,
        label,
        teamId,
      })
    );
  } catch (error) {
    // Ignore error
  }
}
async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(conversationId, status, snoozedUntil) {
  store
    .dispatch('toggleStatus', {
      conversationId,
      status,
      snoozedUntil,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
    });
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return store.getters.getConversationById(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {

  if (check) {
    // Enter selection mode when checking select all
    isSelectionMode.value = true;
    selectAllConversations(check, conversationList);
  } else {
    // When unchecking, clear selections but KEEP selection mode active
    selectAllConversations(check, conversationList);
    // isSelectionMode remains true - user must click X to exit
    isSelectionMode.value = true;
  }
}

// Wrapper functions for selection mode
function handleSelectConversation(conversationId, inboxId, shiftKey = false) {
  // Enter selection mode on first selection
  if (!isSelectionMode.value) {
    isSelectionMode.value = true;
  }

  // If SHIFT is pressed and there are already selected conversations, select range
  if (shiftKey && selectedConversations.value.length > 0) {
    selectRangeConversations(conversationId, conversationList);
  } else {
    selectConversation(conversationId, inboxId);
  }
}

function handleDeSelectConversation(conversationId, inboxId) {
  deSelectConversation(conversationId, inboxId);
}

function exitSelectionMode() {
  resetBulkActions();
  isSelectionMode.value = false;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function getAuthHeaders() {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) return {};
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      'client': sessionInfo['client'],
      'uid': sessionInfo['uid']
    };
  } catch (e) {
    console.error('Error parsing cookie:', e);
    return {};
  }
}

async function fetchAgentNameForInbox(inboxId) {
  if (!inboxId || agentNamesByInbox.value.has(inboxId)) return;
  
  try {
    const headers = getAuthHeaders();
    const response = await wootAPI.get(
      '/api/v1/inboxmeta/fetch_agentis',
      {
        params: { inbox_id: inboxId },
        headers
      }
    );
    
    const agentName = response.data?.agent || '';
    agentNamesByInbox.value.set(inboxId, agentName);
  } catch (error) {
    console.error('Error fetching agent name for inbox', inboxId, ':', error);
    agentNamesByInbox.value.set(inboxId, '');
  }
}

async function fetchAgentNamesForConversations() {
  const uniqueInboxIds = [...new Set(conversationList.value.map(conv => conv.inbox_id))];
  await Promise.all(uniqueInboxIds.map(inboxId => fetchAgentNameForInbox(inboxId)));
}

useEmitter('fetch_conversation_stats', () => {
  
  // Fetch all stats in a single request to prevent UI flickering
  store.dispatch('conversationStats/getAllStats', statsFilters.value);
});

useEmitter('reload_agent_names', () => {
  agentNamesByInbox.value.clear(); // Clear the cache
  fetchAgentNamesForConversations();
});

useEventListener(conversationDynamicScroller, 'scroll', handleScroll);

// Provide functions and state to child components
provide('selectConversation', handleSelectConversation);
provide('deSelectConversation', handleDeSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('updateConversationStatus', toggleConversationStatus);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('isInSelectionMode', isSelectionMode);

onMounted(() => {
  // Ensure default tab is set correctly now that all reactive values are available
  activeAssigneeTab.value = getDefaultTab();

  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
});

watch(activeTeam, () => resetAndFetchData());

watch(
  computed(() => props.conversationInbox),
  () => {
    resetAndFetchData();
  }
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationType),
  () => resetAndFetchData()
);


watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
  fetchAgentNamesForConversations();
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});

// Watch for changes in role or feature flags and update default tab if needed
watch(
  [currentRole, () => isFeatureEnabledonAccount.value(currentAccountId.value, 'hide_all_chats_for_agent')],
  () => {
    const newDefaultTab = getDefaultTab();
    // Only update if we're currently on the old default and it has changed
    if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.TODOS && 
        newDefaultTab === wootConstants.ASSIGNEE_TYPE.MINHAS) {
      activeAssigneeTab.value = newDefaultTab;
    } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.MINHAS && 
               newDefaultTab === wootConstants.ASSIGNEE_TYPE.TODOS) {
      activeAssigneeTab.value = newDefaultTab;  
    }
  }
);
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 bg-n-solid-1 conversations-list-wrap"
    :class="[
      // Desktop: fixed width, hidden based on showConversationList
      'md:w-[360px] 2xl:w-[420px]',
      { 'hidden md:flex': !props.showConversationList },
      // Mobile: full width when active, hidden when not
      props.isMobileActive ? 'w-full' : 'hidden md:flex',
    ]"
  >
    <ChatListHeader
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-status="activeStatus"
      :has-selected-conversation="props.hasSelectedConversation"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
      @toggle-conversation-layout="toggleConversationLayout"
    />

    <slot
      :hasAppliedFiltersOrActiveFolders="hasAppliedFiltersOrActiveFolders"
      :isSelectionMode="isSelectionMode"
    />
    
    <Teleport v-if="showAddFoldersModal" to="#saveFilterTeleportTarget">
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </Teleport>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <!-- Container for smooth transitions between tabs and bulk actions -->
    <div v-if="shouldShowTabsBar" class="relative min-h-[48px]">
      <!-- ChatTypeTabs - slides up when hiding -->
      <div
        class="transition-all duration-300 ease-in-out"
        :class="isSelectionMode ? 'opacity-0 -translate-y-4 pointer-events-none absolute inset-x-0' : 'opacity-100 translate-y-0'"
      >
        <ChatTypeTabs
          v-if="!hasAppliedFiltersOrActiveFolders"
          :items="assigneeTabItems"
          :active-tab="activeAssigneeTab"
          is-compact
          @chat-tab-change="updateAssigneeTab"
        />
      </div>

      <!-- ConversationBulkActions - slides down when appearing -->
      <div
        class="transition-all duration-300 ease-in-out"
        :class="!isSelectionMode ? 'opacity-0 translate-y-4 pointer-events-none absolute inset-x-0' : 'opacity-100 translate-y-0'"
      >
        <ConversationBulkActions
          v-if="isSelectionMode"
          ref="bulkActionsRef"
          :conversations="selectedConversations"
          :all-conversations-selected="allConversationsSelected"
          :selected-inboxes="uniqueInboxes"
          :show-open-action="allSelectedConversationsStatus('open')"
          :show-resolved-action="allSelectedConversationsStatus('resolved')"
          :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
          :is-modal-open="showBulkActionsModal"
          @select-all-conversations="toggleSelectAll"
          @assign-agent="onAssignAgent"
          @update-conversations="onUpdateConversations"
          @assign-labels="onAssignLabels"
          @assign-team="onAssignTeamsForBulk"
          @show-more-actions="handleShowMoreActions"
          @exit-selection-mode="exitSelectionMode"
        />
      </div>
    </div>

    <p
      v-if="!chatListLoading && !conversationList.length"
      class="flex items-center justify-center p-4 overflow-auto"
    >
      Não há conversas ativas neste grupo.
    </p>
    <div
      ref="conversationListRef"
      class="flex-1 overflow-hidden conversations-list hover:overflow-y-auto"
      :class="{ 'overflow-hidden': isContextMenuOpen }"
    >
      <DynamicScroller
        ref="conversationDynamicScroller"
        :items="conversationList"
        :min-item-size="24"
        class="w-full h-full overflow-auto"
      >
        <template #default="{ item, index, active }">
          <!--
            If we encounter resizing issues, we can set the `watchData` prop to true
            this will deeply watch the entire object instead of just size dependencies
            But it can impact performance
          -->
          <DynamicScrollerItem
            :item="item"
            :active="active"
            :data-index="index"
            :size-dependencies="[
              item.messages,
              item.labels,
              item.uuid,
              item.inbox_id,
            ]"
          >
            <div class="pb-2">
              <ConversationItem
                :source="item"
                :label="label"
                :team-id="teamId"
                :folders-id="foldersId"
                :conversation-type="conversationType"
                :show-assignee="showAssigneeInConversationCard"
                :ai-agent-name="agentNamesByInbox.get(item.inbox_id) || null"
                :has-ia-agent="!!(agentNamesByInbox.get(item.inbox_id) && agentNamesByInbox.get(item.inbox_id).length > 0)"
                @select-conversation="handleSelectConversation"
                @de-select-conversation="handleDeSelectConversation"
              />
            </div>
          </DynamicScrollerItem>
        </template>
        <template #after>
          <div v-if="chatListLoading" class="text-center">
            <span class="mt-4 mb-4 spinner" />
          </div>
          <p
            v-else-if="showEndOfListMessage"
            class="p-4 text-center text-slate-400 dark:text-slate-300"
          >
            {{ $t('CHAT_LIST.EOF') }}
          </p>
          <IntersectionObserver
            v-else
            :options="intersectionObserverOptions"
            @observed="loadMoreConversations"
          />
        </template>
      </DynamicScroller>
    </div>
    <Teleport v-if="showAdvancedFilters" to="#conversationFilterTeleportTarget">
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </Teleport>

    <!-- Bulk Actions Modal -->
    <div v-if="showBulkActionsModal">
      <!-- Backdrop -->
      <div class="fixed inset-0 z-[999]" @click="resetModal"></div>

      <!-- Modal Content -->
      <div
        class="fixed z-[1000] bg-white/20 dark:bg-slate-800/20 backdrop-blur-2xl rounded-lg shadow-xl min-w-[250px] max-w-[400px] max-h-[300px] overflow-y-auto overflow-x-hidden flex flex-col p-2 border border-solid border-n-weak"
        :style="{ top: modalPosition.top + 'px', right: modalPosition.right + 'px' }"
      >
        <!-- Transition wrapper for all views -->
        <Transition :name="transitionName" mode="out-in">
          <!-- Main Menu -->
          <div v-if="modalView === 'main'" key="main">
            <button
            class="bulk-modal-item"
            @click="showModalView('labels')"
          >
            <fluent-icon icon="tag" size="15" class="text-n-slate-12" />
            <span class="bulk-modal-text">Atribuir marcador</span>
          </button>

          <button
            class="bulk-modal-item"
            @click="handleEnableAI"
          >
            <i class="i-lucide-zap text-n-slate-12" />
            <span class="bulk-modal-text">Ativar IA</span>
          </button>

          <button
            class="bulk-modal-item"
            @click="showModalView('status')"
          >
            <fluent-icon icon="arrow-redo" size="15" class="text-n-slate-12" />
            <span class="bulk-modal-text">Alterar status</span>
          </button>

          <button
            class="bulk-modal-item"
            @click="showModalView('priority')"
          >
            <i class="i-lucide-flag text-n-slate-12" />
            <span class="bulk-modal-text">Alterar prioridade</span>
          </button>

          <button
            class="bulk-modal-item"
            @click="showModalView('agents')"
          >
            <fluent-icon icon="person" size="15" class="text-n-slate-12" />
            <span class="bulk-modal-text">Atribuir atendente</span>
          </button>

          <button
            class="bulk-modal-item"
            @click="showModalView('teams')"
          >
            <fluent-icon icon="people-team" size="15" class="text-n-slate-12" />
            <span class="bulk-modal-text">Atribuir equipe</span>
          </button>

          <button
            class="bulk-modal-item"
            @click="handleDisableAI"
          >
            <i class="i-lucide-zap-off text-red-600" />
            <span class="bulk-modal-destroy-text">Desativar IA</span>
          </button>

          <button
            class="bulk-modal-item"
            @click="showModalView('removeLabels')"
          >
            <fluent-icon icon="tag" size="15" class="text-red-600" />
            <span class="bulk-modal-destroy-text">Remover marcadores</span>
          </button>

          <button
            class="bulk-modal-item"
            @click="handleDeleteContacts"
          >
            <fluent-icon icon="delete" size="15" class="text-red-600" />
            <span class="bulk-modal-destroy-text">Excluir contatos</span>
          </button>
          </div>

          <!-- Labels View -->
          <div v-else-if="modalView === 'labels'" key="labels" class="flex flex-col h-full">
          <button @click="showModalView('main')" class="bulk-modal-item">
            <i class="i-lucide-chevron-left text-n-slate-12" />
            <span class="bulk-modal-text">Voltar</span>
          </button>
          <div class="overflow-y-auto flex-1">
            <div v-if="!labels || labels.length === 0" class="p-4 text-center text-slate-500">
              Nenhum marcador disponível
            </div>
            <div v-else v-for="label in labels" :key="label.id">
              <button
                class="bulk-modal-item"
                @click="handleLabelSelect(label)"
              >
                <span class="w-3 h-3 rounded-full mr-2" :style="{ backgroundColor: label.color }"></span>
                <span class="bulk-modal-text p-0">{{ label.title }}</span>
              </button>
            </div>
          </div>
          </div>

          <!-- Agents View -->
          <div v-else-if="modalView === 'agents'" key="agents" class="flex flex-col h-full">
          <button @click="showModalView('main')" class="bulk-modal-item">
            <i class="i-lucide-chevron-left text-n-slate-12" />
            <span class="bulk-modal-text">Voltar</span>
          </button>
          <div class="overflow-y-auto flex-1">
            <div v-for="agent in assignableAgents" :key="agent.id || 'none'">
              <button
                class="bulk-modal-item"
                @click="handleAgentSelect(agent)"
              >
                <Thumbnail
                  v-if="agent.id"
                  :src="agent.thumbnail"
                  :status="agent.availability_status"
                  :username="agent.name"
                  size="20px"
                />
                <div v-else class="w-5 h-5 rounded-full bg-woot-100 flex items-center justify-center">
                  <i class="i-lucide-x text-woot-600 text-xs" />
                </div>
                <span class="bulk-modal-text">{{ agent.name }}</span>
              </button>
            </div>
          </div>
          </div>

          <!-- Teams View -->
          <div v-else-if="modalView === 'teams'" key="teams" class="flex flex-col h-full">
          <button @click="showModalView('main')" class="bulk-modal-item">
            <i class="i-lucide-chevron-left text-n-slate-12" />
            <span class="bulk-modal-text">Voltar</span>
          </button>
          <div class="overflow-y-auto flex-1">
            <div v-for="team in allTeams" :key="team.id">
              <button
                class="bulk-modal-item"
                @click="handleTeamSelect(team)"
              >
                <div v-if="team.id === 0" class="w-5 h-5 rounded-full bg-woot-100 flex items-center justify-center">
                  <i class="i-lucide-x text-woot-600 text-xs" />
                </div>
                <i v-else class="i-lucide-users-round text-n-slate-12" />
                <span class="bulk-modal-text">{{ team.name }}</span>
              </button>
            </div>
          </div>
          </div>

          <!-- Status View -->
          <div v-else-if="modalView === 'status'" key="status" class="flex flex-col h-full">
          <button @click="showModalView('main')" class="bulk-modal-item">
            <i class="i-lucide-chevron-left text-n-slate-12" />
            <span class="bulk-modal-text">Voltar</span>
          </button>
          <div class="overflow-y-auto flex-1">
            <button
              class="bulk-modal-item"
              @click="handleStatusSelect('open')"
            >
              <fluent-icon icon="arrow-redo" size="15" class="text-n-slate-12" />
              <span class="bulk-modal-text">Reabrir</span>
            </button>
            <button
              class="bulk-modal-item"
              @click="handleStatusSelect('resolved')"
            >
              <fluent-icon icon="checkmark" size="15" class="text-n-slate-12" />
              <span class="bulk-modal-text">Resolver</span>
            </button>
            <button
              class="bulk-modal-item"
              @click="handleStatusSelect('snoozed')"
            >
              <fluent-icon icon="send-clock" size="15" class="text-n-slate-12" />
              <span class="bulk-modal-text">Adiar</span>
            </button>
          </div>
          </div>

          <!-- Priority View -->
          <div v-else-if="modalView === 'priority'" key="priority" class="flex flex-col h-full">
          <button @click="showModalView('main')" class="bulk-modal-item">
            <i class="i-lucide-chevron-left text-n-slate-12" />
            <span class="bulk-modal-text">Voltar</span>
          </button>
          <div class="overflow-y-auto flex-1">
            <button
              class="bulk-modal-item"
              @click="handlePrioritySelect(null)"
            >
              <i class="i-lucide-x text-n-slate-12" />
              <span class="bulk-modal-text">Nenhuma</span>
            </button>
            <button
              class="bulk-modal-item"
              @click="handlePrioritySelect('low')"
            >
              <i class="i-lucide-flag text-green-600" />
              <span class="bulk-modal-text">Baixa</span>
            </button>
            <button
              class="bulk-modal-item"
              @click="handlePrioritySelect('medium')"
            >
              <i class="i-lucide-flag text-yellow-600" />
              <span class="bulk-modal-text">Média</span>
            </button>
            <button
              class="bulk-modal-item"
              @click="handlePrioritySelect('high')"
            >
              <i class="i-lucide-flag text-orange-600" />
              <span class="bulk-modal-text">Alta</span>
            </button>
            <button
              class="bulk-modal-item"
              @click="handlePrioritySelect('urgent')"
            >
              <i class="i-lucide-flag text-red-600" />
              <span class="bulk-modal-text">Urgente</span>
            </button>
          </div>
          </div>

          <!-- Remove Labels View -->
          <div v-else-if="modalView === 'removeLabels'" key="removeLabels" class="flex flex-col h-full">
            <button @click="showModalView('main')" class="bulk-modal-item">
              <i class="i-lucide-chevron-left text-n-slate-12" />
              <span class="bulk-modal-text">Voltar</span>
            </button>

            <!-- Remove all option -->
            <button
              class="bulk-modal-item"
              @click="handleRemoveAllLabels"
            >
              <fluent-icon icon="tag" size="15" class="text-red-600" />
              <span class="bulk-modal-text">Todos</span>
            </button>

            <!-- Individual labels -->
            <div class="overflow-y-auto flex-1">
              <div v-if="!labels || labels.length === 0" class="p-4 text-center text-slate-500">
                Nenhum marcador disponível
              </div>
              <div v-else v-for="label in labels" :key="label.id">
                <button
                  v-if="label.title !== 'atendimento-humano'"
                  class="bulk-modal-item"
                  @click="handleRemoveSingleLabel(label.title)"
                >
                  <span class="w-3 h-3 rounded-full mr-2" :style="{ backgroundColor: label.color }"></span>
                  <span class="bulk-modal-text">{{ label.title }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Confirmation View -->
          <div v-else-if="modalView === 'confirmation'" key="confirmation" class="flex flex-col h-full">
            <div class="p-3">
              <div class="flex items-center mb-3">
                <i class="i-lucide-alert-triangle text-ruby-600 text-xl mr-2" />
                <h3 class="text-base font-semibold text-n-slate-12">Confirmar ação</h3>
              </div>

              <p class="text-sm text-n-slate-12 mb-4">
                {{ confirmationState.message }}
              </p>

              <div class="flex gap-2">
                <button
                  @click="cancelConfirmation"
                  class="flex-1 px-3 py-2 text-sm border border-n-weak rounded-lg hover:bg-n-alpha-2 text-n-slate-12 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  @click="executeConfirmedAction"
                  class="flex-1 px-3 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Bulk modal consistent styles */
.bulk-modal-item {
  @apply flex items-center p-1.5 rounded-lg cursor-pointer ml-0 w-full text-left hover:bg-n-alpha-1 dark:hover:bg-n-slate-6;
}

.bulk-modal-text {
  @apply pl-3 text-sm text-n-slate-12;
}

.bulk-modal-destroy-text {
  @apply pl-3 text-sm text-ruby-600;
}

/* Transition styles for modal views */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.1s ease-out;
}

.slide-left-enter-from {
  transform: translateX(30px);
  opacity: 0;
}

.slide-left-leave-to {
  transform: translateX(-30px);
  opacity: 0;
}

.slide-right-enter-from {
  transform: translateX(-30px);
  opacity: 0;
}

.slide-right-leave-to {
  transform: translateX(30px);
  opacity: 0;
}

/* Custom scrollbar styles - lighter and subtle */
.conversations-list ::-webkit-scrollbar {
  width: 8px;
}

.conversations-list ::-webkit-scrollbar-track {
  background: transparent;
}

.conversations-list ::-webkit-scrollbar-thumb {
  background-color: rgb(226 232 240 / 0.5); /* Light gray with transparency */
  border-radius: 4px;
}

/* Dark mode scrollbar */
.dark .conversations-list ::-webkit-scrollbar-thumb {
  background-color: rgb(71 85 105 / 0.5); /* Subtle gray for dark mode */
}

/* Firefox support */
.conversations-list {
  scrollbar-width: thin;
  scrollbar-color: rgb(231 231 231 / 0.5) transparent;
}

.dark .conversations-list {
  scrollbar-color: rgb(71 85 105 / 0.5) transparent;
}
</style>
